import type { ActionFunctionArgs, MetaFunction } from "@remix-run/node";
import { json } from "@remix-run/node";
import { Globe } from "lucide-react";
import { Form, useActionData, useNavigate, useNavigation } from "@remix-run/react";
import { Heading, Text } from "@/components/ui/typography";
import { LoaderButton } from "@/components/ui/buttons";
import { AbstractLink } from "@/components/ui/links/Links.tsx";
import { useEffect } from "react";

// Temporary access code - change this to your desired code
    const ACCESS_CODE = "gct_@2025!";
// Token to store in localStorage
const AUTH_TOKEN = "0e01f6ae9cd9fd77bb6f0fe41a8d915e5f528bae";

export const meta: MetaFunction = () => {
	return [
		{ title: "Gift Come True - Vendors" },
		{ name: "description", content: "Welcome to Gift Come True! This is the admin panel for which you may not have access." },
	];
};

export const action = async ({ request }: ActionFunctionArgs) => {
	const formData = await request.formData();
	const code = formData.get("code") as string;

	if (code === ACCESS_CODE) {
		return json({ success: true, token: AUTH_TOKEN });
	}

	return json({ success: false, error: "Invalid access code" }, { status: 401 });
};

export default function Index() {
	const navigation = useNavigation();
	const actionData = useActionData<typeof action>();
	const navigate = useNavigate();

	// Check if already authenticated on mount
	useEffect(() => {
		const token = localStorage.getItem("gct_auth_token");
		if (token === AUTH_TOKEN) {
			navigate("/dashboard");
		}
	}, [navigate]);

	// Handle successful login
	useEffect(() => {
		if (actionData?.success && actionData?.token) {
			localStorage.setItem("gct_auth_token", actionData.token);
			navigate("/dashboard");
		}
	}, [actionData, navigate]);

	return (
		<section className="bg-white h-screen w-full relative flex items-center justify-center bg-gradient-to-bl from-slate-50 to-slate-100">
			<div className="w-full mx-auto max-w-[1140px] relative flex items-center justify-center p-10">
				<div className="mr-auto place-self-center lg:col-span-5">

					<div className="w-full text-right">
						<div className="inline-flex bg-blue-100 text-blue-800 text-sm font-medium me-2 px-2.5 py-1 rounded">Admin</div>
					</div>
					<div className="my-1">
						<Heading tag="h1" className="max-w-2xl mb-4 text-4xl font-extrabold tracking-tight leading-none md:text-5xl xl:text-6xl">Gift Come True</Heading>
						<Text type="secondary">
							Emotion ambassador. From gifting experiences to creating unforgettable moments for your and your
							loved ones.
						</Text>
					</div>

					<div className="relative w-full mt-6">
						<Form method="POST" className="flex flex-col gap-4 max-w-sm">
							<div>
								<label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
									Access Code
								</label>
								<input
									type="password"
									id="code"
									name="code"
									required
									className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
									placeholder="Enter access code"
								/>
								{actionData?.error && (
									<p className="mt-1 text-sm text-red-600">{actionData.error}</p>
								)}
							</div>
							<div className="flex items-center gap-3">
								<LoaderButton
									isLoading={navigation.state === 'submitting'}
									type="submit"
									className="inline-flex items-center justify-center px-5 py-3 text-base font-medium text-center text-white rounded-lg bg-primary-700 hover:bg-primary-800 focus:ring-4 focus:ring-primary-300"
									labels={{
										loading: 'Verifying...',
										idle: 'Login',
										completed: 'Redirecting...'
									}}
								>
									Login
								</LoaderButton>
								<AbstractLink href="https://giftcometrue.com" className="inline-flex gap-2 items-center justify-center px-5 py-3 text-base font-medium text-center text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-100 focus:ring-4 focus:ring-gray-100">
									<Globe className="text-neutral-500" size={20}/>
									Website
								</AbstractLink>
							</div>
						</Form>
					</div>
				</div>
				<div className="hidden lg:mt-0 lg:col-span-7 lg:flex h-full">
					<img className="block w-full h-auto object-center object-cover" src="/media/gct-login-illustration.svg" alt="mockup"/>
				</div>
			</div>
		</section>
	);
}

